import { FormGroup } from '@angular/forms';
import { LooseObject } from '../objects/loose-object';
import { BehaviorSubject } from 'rxjs';
import { data_is_true, OptionalValue } from '../shared/utils/common';
import { StudioFormTab } from '../services/form.service';

export class Form<T>{
    default_value: T;
    key: string;
    label: string;
    controlType: string;
    space: number;
    required: boolean = false;
    readonly: boolean = false;
    validator: boolean;
    max_length: string;
    min_length: string;
    is_hidden: boolean;
    precision: number[]|null;
    is_admin: boolean = false;
    has_primary: boolean;
    note: string;
    decimal_places?: number;

    /**
     * Indicates that the field is a multiple value field
     */
    readonly multiple: boolean;

    /**
     * Contains the options to be used when creating a proper control instance for this field
     */
    readonly custom_form_options: CustomFormOptions;

    /**
     * Dictates if the given form field should hide its label. This is useful
     * when you have a custom field component and that component is responsible for displaying the
     * appropriate label for the field
     */
    readonly hide_label: boolean;

    /// contains the availability condition of the current field
    readonly depends_on?: string;

    readonly form_readonly?: boolean;

    constructor(options: {
        default_value?: T,
        key?: string,
        label?: string,
        controlType?: string,
        space?: number,
        required?: boolean,
        readonly?: boolean,
        validator?: boolean,
        is_hidden?: boolean,
        max_length?: string,
        min_length?: string,
        precision?: number[],
        is_admin?: boolean,
        has_primary?: boolean,
        note?: string,
        multiple?: boolean,
        custom_form_options?: CustomFormOptions,
        hide_label?: boolean,
        decimal_places?: number,
        depends_on?: string,
        form_readonly?: boolean;
      } = {}) {
        this.default_value = options.default_value,
        this.key = options.key,
        this.label = options.label,
        this.controlType = options.controlType,
        this.space = (options.space == 2) ? 12 : 6,
        this.required = options.required || this.required,
        this.readonly = options.readonly || this.readonly,
        this.validator = options.validator
        this.is_hidden = options.is_hidden || false
        this.max_length = options.max_length
        this.min_length = options.min_length
        this.precision = options.precision
        this.is_admin = options.is_admin || this.is_admin
        this.has_primary = options.has_primary || false
        this.note = options.note;
        this.multiple = options.multiple;
        this.custom_form_options = options.custom_form_options;
        this.hide_label = options.hide_label || false;
        this.decimal_places = options.decimal_places;
        this.depends_on = options.depends_on;
        this.form_readonly = options.form_readonly;
    }

    toggleFieldAvailability(available: boolean, opts: {
      action: AvailabilityAction,
    }): void {
      if (opts.action === 'readonly') {
        this.readonly = ! available;
      } else if (opts.action === 'hide') {
        this.is_hidden = available;
      }
  }
}

export type AvailabilityAction = 'readonly' | 'hide';

export type CustomFormOptions = {
  /**
   * The selector of the field component to be used as a custom field for the current form
   */
  component_selector: string;
  /**
   * Dictates the control for this custom form field is an object.
   */
  is_object?: boolean;
};

export type FormMode = 'edit' | 'add';

export interface CustomEditFieldComponent<T = any> {
  /**
   * Set the current mode of the form
   */
  setFormMode(formMode: FormMode): void;
  /**
   * Sets the instance of the parent form
   */
  setParentForm(parent: FormGroup): void;
  /**
   * Sets the current field which represents by the custom field component
   */
  setField(field: Form<T>): void;
  /**
   * Sets the additional data you may want to pass to your custom field component. This field is optional.
   */
  setAdditionalData?(data: LooseObject): void;
  /**
   * set current form tabs
   */
  setTabs?(tabs: StudioFormTab[]): void;
}

export interface CustomFieldViewComponent<T = any, D = Record<string, any>> {
  /**
   * Sets the current field which represents by the custom field component
   */
  setField(field: Form<T>): void;

  setAdditionalData?(data: LooseObject): void;
  setViewData?(data: D): void;

  setSettings?(settings: ViewCustomFieldSettings): void;
}

export class BaseCustomEditFieldComponent<T = any, A = Record<string, any>> implements CustomEditFieldComponent<T> {
  protected readonly mode$ = new BehaviorSubject<FormMode>('add');
  protected readonly field$ = new BehaviorSubject<OptionalValue<Form<any>>>(null);
  protected readonly parent$ = new BehaviorSubject<OptionalValue<FormGroup>>(null);
  protected readonly data$ = new BehaviorSubject<OptionalValue<A & CustomEditFieldInputData>>(null);
  protected readonly tabs$ = new BehaviorSubject<StudioFormTab[]>([]);

  /**
   * @inheritdoc
   */
  setFormMode = (mode: FormMode) => this.mode$.next(mode);

  /**
   * @inheritdoc
   */
  setParentForm = (parent: FormGroup) => this.parent$.next(parent);

  /**
   * @inheritdoc
   */
  setField = (field: Form<any>) => this.field$.next(field);

  /**
   * @inheritdoc
   */
  setAdditionalData = (data: OptionalValue<A & CustomEditFieldInputData>) => this.data$.next(data);

  /**
   * @inheritdoc
   */
  setTabs = (tabs: StudioFormTab[]) => this.tabs$.next(tabs);
}

export type CustomEditFieldInputData = {
  record_id?: string;
}

export class BaseCustomFieldViewComponent<T = any, D = Record<string, any>> implements CustomFieldViewComponent<T, D> {
  protected readonly field$ = new BehaviorSubject<Form<T>>(null);
  protected readonly data$ = new BehaviorSubject<OptionalValue<D>>(null);
  protected readonly settings$ = new BehaviorSubject<ViewCustomFieldSettings>({});

  setField(field: Form<T>): void {
    this.field$.next(field);
  }

  setViewData(data: D): void {
    this.data$.next(data);
  }

  setSettings(settings: ViewCustomFieldSettings): void {
    this.settings$.next(settings);
  }
}

export type ViewCustomFieldSettings = {
  viewed_as_dialog?: boolean;
};